import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ChevronLeft, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const Help = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      category: 'Reservaciones',
      questions: [
        {
          q: '¿Cómo reservo una actividad?',
          a: 'Encuentra la actividad que te interesa y presiona "Reservar". Si es gratuita, tu lugar se confirma al instante. Si tiene costo, verás los datos de pago del creador (link de pago, CLABE o datos bancarios) para que realices la transferencia. Puedes subir tu comprobante de pago directamente en el checkout después de pagar, o si prefieres hacerlo más tarde, ve a "Mis Actividades", ubica tu reservación y desde ahí podrás subir tu comprobante. Una vez que el creador lo revise, confirmará tu lugar.'
        },
        {
          q: '¿Cuánto tiempo tengo para pagar?',
          a: 'El tiempo para pagar depende de los ajustes que cada creador configura para sus actividades. Los rangos posibles son:\n\n• Más de 48h antes de la actividad → hasta 24 horas para pagar\n• Entre 48h y 24h antes → hasta 20 horas para pagar\n• Entre 24h y 6h antes → hasta 6 horas para pagar\n• Entre 6h y 3h antes → hasta 3 horas para pagar\n• Menos de 3h antes → reservas cerradas\n\nCada creador elige cuáles de estos rangos acepta, por lo que los plazos pueden variar entre actividades. Al momento de reservar verás el tiempo exacto que tienes para completar tu pago.'
        },
        {
          q: '¿Cómo subo mi comprobante de pago?',
          a: 'Tienes dos opciones:\n\n1. En el checkout: Después de realizar tu pago, puedes subir tu comprobante directamente en el paso de confirmación del checkout.\n2. Desde Mis Actividades: Si prefieres pagar más tarde, ve a "Mis Actividades", busca tu reservación pendiente y presiona el botón para subir tu comprobante.\n\nEn ambos casos, el creador recibirá una notificación y podrá confirmar tu pago desde su panel.'
        },
        {
          q: '¿Cómo cancelo una reservación?',
          a: 'Ve a "Mis Actividades", encuentra tu reservación y presiona "Cancelar reserva". El sistema te mostrará el porcentaje de reembolso que te corresponde según las reglas del creador, basado en cuántas horas faltan para la actividad. Si te corresponde reembolso, el creador lo recibirá en su panel y realizará la devolución a tu método de reembolso configurado (CLABE o PayPal).'
        },
        {
          q: '¿Qué pasa si la actividad se llena?',
          a: 'Si una actividad alcanza su cupo máximo, podrás unirte a la lista de espera. Si se libera un lugar o el creador agrega nuevas fechas, recibirás una notificación para que puedas confirmar tu interés.'
        },
        {
          q: '¿Puedo usar cupones de descuento?',
          a: 'Sí. Si el creador de la actividad ofrece cupones, podrás ingresarlo al momento de reservar en el checkout. El descuento se aplicará automáticamente al total. Los cupones tienen fecha de vencimiento y límite de usos.'
        }
      ]
    },
    {
      category: 'Cuenta',
      questions: [
        {
          q: '¿Cómo instalar utentik como app en mi celular?',
          a: 'utentik funciona como una app directamente desde tu navegador.\n\niPhone (Safari):\n1. Abre utentik.com en Safari\n2. Toca el botón de compartir (cuadrado con flecha)\n3. Selecciona "Agregar a pantalla de inicio"\n4. Toca "Agregar"\n\nAndroid (Chrome):\n1. Abre utentik.com en Chrome\n2. Toca el menú de tres puntos en la esquina superior\n3. Selecciona "Instalar app" o "Agregar a pantalla de inicio"\n4. Confirma la instalación'
        },
        {
          q: '¿Cómo creo una cuenta?',
          a: 'Puedes registrarte con tu correo electrónico o con tu cuenta de Google. Al registrarte, elige si quieres ser "Usuario" (para reservar actividades) o "Creador" (para publicar tus propias experiencias). Recibirás un correo de verificación para activar tu cuenta.'
        },
        {
          q: '¿Cómo configuro mi método de reembolso?',
          a: 'Ve a tu Perfil y busca la sección "Método de reembolso". Puedes agregar tu CLABE interbancaria (se detecta automáticamente el banco) y/o tu link de PayPal o Mercado Pago. Esto es necesario para que, en caso de cancelación, el creador pueda devolverte tu dinero.'
        },
        {
          q: '¿Cómo sigo a un creador?',
          a: 'En el perfil público de cualquier creador encontrarás el botón "Seguir". Al seguir a un creador, recibirás notificaciones por correo y push cada vez que publique una nueva actividad.'
        },
        {
          q: '¿Cómo me convierto en creador?',
          a: 'Al registrarte puedes elegir el rol de "Creador". Si ya tienes cuenta como usuario, ve a tu perfil y cambia tu rol a creador. Tendrás acceso al Panel de Creador donde podrás publicar actividades, gestionar reservas y recibir pagos.'
        },
        {
          q: '¿Cómo cambio mi contraseña?',
          a: 'Cierra sesión y usa la opción "Olvidé mi contraseña" en la pantalla de inicio de sesión. Recibirás un correo para restablecerla.'
        }
      ]
    },
    {
      category: 'Para Creadores',
      questions: [
        {
          q: '¿Cómo publico una actividad?',
          a: 'Desde tu Panel de Creador, presiona "Crear actividad". Completa los campos: título, descripción corta y larga, categorías, fechas con horario y cupo por fecha, ubicación (estado, ciudad, colonia, dirección), precio, y opcionalmente FAQs y tags. Puedes agregar hasta 5 fotos. Las actividades gratuitas se publican directamente; las de pago requieren que configures tus métodos de pago primero.'
        },
        {
          q: '¿Cómo configuro mis métodos de pago?',
          a: 'En tu Panel de Creador → pestaña "Pagos" → "Métodos para recibir pagos". Puedes agregar:\n\n• Link de pago (PayPal, Mercado Pago, etc.)\n• Datos bancarios (banco, titular, CLABE interbancaria)\n\nEstos datos se muestran al usuario cuando reserva una actividad de pago para que realice la transferencia.'
        },
        {
          q: '¿Cómo funcionan los ajustes de pagos?',
          a: 'En "Pagos" → "Ajustes de pagos" puedes controlar hasta qué momento se aceptan reservas antes del inicio de tu actividad. Cada rango tiene un tiempo máximo para que el usuario pague:\n\n• 48h-24h antes → 20 horas para pagar\n• 24h-6h antes → 6 horas para pagar\n• 6h-3h antes → 3 horas para pagar\n\nPuedes activar o desactivar cada rango según te convenga.'
        },
        {
          q: '¿Cómo configuro las reglas de cancelación?',
          a: 'En "Pagos" → "Reglas de cancelación" defines los porcentajes de reembolso cuando un usuario cancela. Por ejemplo:\n\n• Más de 48h antes → 100% de reembolso\n• Más de 24h antes → 50% de reembolso\n• Menos de 24h antes → cancelación bloqueada\n\nEstas reglas se aplican automáticamente cuando un usuario cancela su reserva.'
        },
        {
          q: '¿Cómo confirmo un pago?',
          a: 'Cuando un usuario sube su comprobante de pago, recibirás una notificación "Comprobante de pago enviado". Al dar clic te llevará directamente a la sección de pendientes de esa actividad. Ahí puedes:\n\n• Ver el comprobante (botón "Ver comprobante")\n• Confirmar el pago\n• Marcar como "No encontrado" si no identificas el pago (el usuario tendrá 24h para reenviar)'
        },
        {
          q: '¿Cómo creo cupones de descuento?',
          a: 'Al crear o editar una actividad de pago, encontrarás la sección de cupones. Puedes crear cupones con un código personalizado, descuento en pesos (MXN), límite de usos y fecha de vencimiento. Los usuarios podrán aplicar el cupón al reservar.'
        },
        {
          q: '¿Cómo veo mis estadísticas?',
          a: 'En la pestaña "Analytics" de tu panel encontrarás métricas de tus actividades activas y pasadas: número de inscritos, ingresos totales y por actividad. En la pestaña "General" verás un resumen con filtros por año, mes y actividad, incluyendo pagos confirmados, pendientes y total esperado.'
        },
        {
          q: '¿Cómo funciona mi perfil público?',
          a: 'Tu perfil público es visible en utentik.com/creator/tu-nombre. Incluye tu foto, bio, ubicación, redes sociales (Instagram, TikTok, YouTube, Facebook), tus actividades próximas, galería de fotos (hasta 30 imágenes) y reseñas. Puedes previsualizarlo desde tu perfil antes de publicar cambios.'
        },
        {
          q: '¿Qué pasa si cancelo una actividad con inscritos?',
          a: 'Al cancelar una actividad, todos los inscritos recibirán una notificación por correo y push. Los usuarios que enviaron comprobante de pago aparecerán en tu panel como "Reembolso pendiente" con sus datos de CLABE o PayPal para que puedas realizar la devolución.'
        }
      ]
    },
    {
      category: 'Pagos y Reembolsos',
      questions: [
        {
          q: '¿utentik cobra comisión?',
          a: 'Actualmente utentik es una plataforma gratuita tanto para usuarios como para creadores. No cobramos comisiones por las transacciones.'
        },
        {
          q: '¿Cómo funciona el ciclo de pago?',
          a: 'Al reservar una actividad de pago:\n\n1. Recibes los datos del creador (link de pago, CLABE, banco)\n2. Realizas el pago por tu cuenta\n3. Subes tu comprobante de pago (en el checkout o desde Mis Actividades)\n4. El creador revisa y confirma tu pago\n5. Tu lugar queda confirmado\n\nSi el plazo vence sin que subas tu comprobante, tu reservación se cancela automáticamente.'
        },
        {
          q: '¿Cómo funciona el reembolso si cancelo?',
          a: 'El porcentaje de reembolso depende de las reglas del creador y cuántas horas faltan para la actividad. Al cancelar, el sistema te muestra exactamente cuánto te corresponde. El creador verá tus datos de reembolso (CLABE o PayPal que configuraste en tu perfil) y realizará la devolución. Asegúrate de tener tu método de reembolso configurado antes de reservar.'
        },
        {
          q: '¿Qué pasa si el creador no confirma mi pago?',
          a: 'Si después de 48 horas el creador no confirma tu pago y tú subiste comprobante, tu reservación se marcará como vencida. El creador recibirá un aviso de que debe realizar el reembolso. Si tienes problemas, envía un correo a soporte@utentik.com con los detalles de tu caso.'
        },
        {
          q: '¿Qué hago si el creador no responde?',
          a: 'Si tienes problemas para contactar a un creador, envía un correo a soporte@utentik.com con los detalles de tu caso y te ayudaremos a mediar la situación.'
        },
        {
          q: '¿Qué pasa si vence el tiempo para subir el comprobante de pago?',
          a: 'Tu reservación se cancela automáticamente y el lugar queda disponible para otros usuarios. Si aún deseas asistir, puedes volver a reservar siempre que haya cupo disponible y el plazo de reservas siga abierto.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pb-32 md:pb-8 bg-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 safe-top">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/prelanzamiento" onClick={() => window.scrollTo(0,0)}>
              <img src="/utentik-logo.png" alt="utentik" className="h-5" />
            </Link>
            <span className="text-gray-200">|</span>
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-gray-400 hover:text-gray-500 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
          <h1 className="font-rubik font-semibold">Ayuda</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Hero */}
        <div className="text-center mb-8">
          <h2 className="font-rubik text-2xl font-bold mb-2">¿En qué podemos ayudarte?</h2>
          <p className="text-gray-400">Encuentra respuestas a las preguntas más frecuentes</p>
        </div>

        {/* FAQs */}
        <div className="space-y-8">
          {faqs.map((section) => (
            <div key={section.category}>
              <h3 className="font-rubik font-semibold text-lg mb-4 text-primary">
                {section.category}
              </h3>
              <Accordion type="single" collapsible className="w-full">
                {section.questions.map((faq, idx) => (
                  <AccordionItem 
                    key={faq.q} 
                    value={`${section.category}-${idx}`}
                    className="border-b border-gray-100"
                  >
                    <AccordionTrigger className="text-left py-4 hover:no-underline font-medium">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-400 pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        {/* Contact section */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <div className="text-center">
            <h3 className="font-rubik font-semibold text-lg mb-2">¿No encontraste lo que buscabas?</h3>
            <p className="text-gray-400 mb-6">Contáctanos y te ayudaremos</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                className="rounded-xl px-6 py-3 h-auto"
                onClick={() => window.location.href = 'mailto:soporte@utentik.com'}
              >
                <Mail className="w-5 h-5 mr-2" />
                soporte@utentik.com
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
